import axios from "axios";

const BASE_URL = process.env.REACT_APP_API_BASE_URL + "/api/v1";

const instance = axios.create({
    baseURL: BASE_URL,
    withCredentials: true, // envia os cookies HttpOnly automaticamente
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve();
        }
    });
    failedQueue = [];
};

const LOGIN_PATH = "/pages/authentication/login";
const NO_REDIRECT_PATHS = ["/pages/landing", LOGIN_PATH, "/pages/acesso-negado"];

const redirectToLogin = () => {
    if (!NO_REDIRECT_PATHS.some((p) => window.location.pathname.startsWith(p))) {
        window.location.href = LOGIN_PATH;
    }
};

const _internalRefresh = async () => {
    try {
        // O refresh token é lido pelo backend diretamente do cookie HttpOnly
        const response = await instance.post("authentication/token/refresh/");
        return { statusCode: response.status };
    } catch (error) {
        return {
            error: {
                message: error.response?.data?.detail || error.message || "Erro ao tentar refresh.",
                code: error.response?.data?.code || null,
            },
            statusCode: error.response?.status || null,
        };
    }
};

instance.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        if (
            error.response?.status === 403 &&
            error.response?.data?.code === 'no_system_access'
        ) {
            window.location.href = '/pages/acesso-negado';
            return Promise.reject(error);
        }

        if (
            error.response &&
            error.response.status === 401 &&
            !originalRequest._retry &&
            !originalRequest.url.includes("authentication/token/refresh/")
        ) {
            originalRequest._retry = true;

            if (!isRefreshing) {
                isRefreshing = true;

                try {
                    const result = await _internalRefresh();

                    if (result.error) {
                        isRefreshing = false;
                        processQueue(result.error);
                        redirectToLogin();
                        return Promise.reject(error);
                    }

                    // Refresh bem-sucedido — o backend já setou o novo cookie
                    isRefreshing = false;
                    processQueue(null);
                    return instance(originalRequest);
                } catch (refreshError) {
                    isRefreshing = false;
                    processQueue(refreshError);
                    redirectToLogin();
                    return Promise.reject(error);
                }
            } else {
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                }).then(() => instance(originalRequest))
                  .catch(err => Promise.reject(err));
            }
        }

        return Promise.reject(error);
    }
);

export const api = async ({
    endpoint,
    method = "GET",
    data,
    withAttachment = false,
}) => {
    const headers = {};

    if (withAttachment) {
        headers["Content-Type"] = "multipart/form-data";
    } else if (method !== "GET") {
        headers["Content-Type"] = "application/json";
    }

    try {
        const config = {
            method,
            url: endpoint,
            headers,
        };

        if (method === "GET") {
            config.params = data;
        } else {
            config.data = data;
        }

        const request = await instance(config);
        return { data: request.data, statusCode: request.status };
    } catch (error) {
        console.error(`API Error for ${endpoint}:`, error.response || error);
        return {
            error: {
                message: error.response?.data?.detail || error.message || "Ocorreu um erro inesperado",
                code: error.response?.data?.code || null,
                data: error.response?.data || null,
            },
            statusCode: error.response?.status || null,
            response: error.response,
        };
    }
};
